import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import {
  TableChartOutlined as TableChartOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from '@mui/icons-material';
import StatusBox from '@ui-parts/StatusBox';
import RejectedReasonBox from '@ui-parts/RejectedReasonBox';

import { extractDateFromIsoDate, isAdminPath } from '@functional';
import { SURVEY_STATUS_COLORS, PALETTE } from '~/assets/styles/base';
import { SURVEY_FORM_TYPE, SurveyStatus, SURVEY_STATUSES } from '~/common/constants';
import Button from '~/components/ui-elements/Button';
import AlertSuccess from '~/components/ui-parts/AlertSuccess';
import { ROUTES } from '~/constants/routes';
import ReceiptRequestsModal from './ReceiptRequestsModal';

type SurveyDetailContainerProps = {
  $status: SurveyStatus;
};

const SurveyDetailContainer = styled.div<SurveyDetailContainerProps>`
  padding: 20px;
  width: 100%;
  background: white;
  border: solid 1px;
  border-color: ${({ $status }) => SURVEY_STATUS_COLORS[$status]};
`;

type SurveyMemoProps = {
  component: string;
};
const SurveyMemo = styled(Typography)<SurveyMemoProps>`
  color: #425f77;
  margin-left: 15px;
`;

type DetailDiscriptionTypographyProps = {
  component: string;
};
const DetailDiscriptionTypography = styled(Typography)<DetailDiscriptionTypographyProps>`
  margin-bottom: 10px;
`;

type DeliveryDateProps = {
  component: string;
};
const DeliveryDate = styled(Typography)<DeliveryDateProps>`
  background: ${PALETTE.bg.main};
  padding: 10px;
  width: 330px;
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SurveyStatusDetailContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StatusOptions = styled.div`
  display: flex;
  gap: 10px;
`;

type Props = {
  survey: {
    id: number;
    surveyStatusId: SurveyStatus;
    created: string;
    modified: string;
    deliveryDate: string | null;
    surveyStatusName: string;
    rejectedReason: string;
    type: number;
  };
};

const SurveyStatusDetailBox: React.FC<Props> = ({ survey }): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openReceiptRequestsModal, setOpenReceiptRequestsModal] = useState(false);
  const [alert, setAlert] = useState(false);

  // 納品日時は承認済みの時のみ表示する。下書きなどではdeliveryDateがnullになる。
  const getDate =
    survey.surveyStatusId === SURVEY_STATUSES.APPROVED
      ? extractDateFromIsoDate(survey.deliveryDate)
      : null;

  const handleReceiptRequestAlert = (isOpen: boolean) => setAlert(isOpen);

  const onClickWebAggregation = () => {
    if (survey.id) {
      navigate(ROUTES.USER.WEB_AGGREGATION.replace(':surveyId', survey.id.toString()));
    }
  };

  useEffect(() => {
    if (alert) {
      setTimeout(() => {
        setAlert(false);
      }, 3000);
    }
  }, [alert]);

  return (
    <>
      <SurveyStatusDetailContainer>
        <StatusOptions>
          <StatusBox label={survey.surveyStatusName} status={survey.surveyStatusId} />

          {!isAdminPath() &&
            survey.type === SURVEY_FORM_TYPE.ORIGINAL_FORM &&
            (survey.surveyStatusId === SURVEY_STATUSES.COLLECTING ||
              survey.surveyStatusId === SURVEY_STATUSES.COMPLETED) && (
              <Button
                dataTestId="web-aggregation-button"
                variant="outlined"
                size="small"
                colorType="navy"
                startIcon={<TableChartOutlinedIcon />}
                handleClick={onClickWebAggregation}
                sx={{
                  borderRadius: '20px',
                }}
              >
                <Typography variant="body4">{t('survey.webAggregate')}</Typography>
              </Button>
            )}
          {!isAdminPath() && survey.surveyStatusId === SURVEY_STATUSES.COMPLETED && (
            <>
              <Button
                dataTestId="receipt-requests-button"
                variant="outlined"
                size="small"
                colorType="navy"
                startIcon={<DescriptionOutlinedIcon />}
                handleClick={() => setOpenReceiptRequestsModal(true)}
                sx={{
                  borderRadius: '20px',
                }}
              >
                <Typography variant="body4">{t('receiptRequests.title')}</Typography>
              </Button>
              <ReceiptRequestsModal
                open={openReceiptRequestsModal}
                handleClose={() => setOpenReceiptRequestsModal(false)}
                surveyId={survey.id}
                setAlert={handleReceiptRequestAlert}
              />
              <AlertSuccess
                data-testid="ReceiptRequestsApiAlert"
                head={t('receiptRequests.alertSuccess.head')}
                body={t('receiptRequests.alertSuccess.body')}
                open={alert}
                onClose={() => setAlert(false)}
              />
            </>
          )}
        </StatusOptions>
        <FlexContainer>
          <SurveyMemo variant="note1" component="p">
            {t('survey.detailBoxSurveyId')}
            <Typography variant="note1" data-testid="uiParts-SurveyStatusDetailBox-surveyId">
              {survey.id}
            </Typography>
          </SurveyMemo>
          <SurveyMemo variant="note1" component="p">
            {t('survey.detailBoxCreated')}
            <Typography variant="note1" data-testid="uiParts-SurveyStatusDetailBox-created">
              {format(parseISO(survey.created), 'yyyy/MM/dd')}
            </Typography>
          </SurveyMemo>
          <SurveyMemo variant="note1" component="p">
            {t('survey.detailBoxModified')}
            <Typography variant="note1" data-testid="uiParts-SurveyStatusDetailBox-modified">
              {format(parseISO(survey.modified), 'yyyy/MM/dd')}
            </Typography>
          </SurveyMemo>
        </FlexContainer>
      </SurveyStatusDetailContainer>
      {survey.surveyStatusId === SURVEY_STATUSES.APPROVED && (
        <SurveyDetailContainer
          $status={survey.surveyStatusId}
          data-testid="uiParts-SurveyStatusDetailBox-SurveyDetailContainer"
        >
          <DetailDiscriptionTypography
            component="p"
            variant="bodyDefault"
            data-testid="uiParts-SurveyStatusDetailBox-Text"
          >
            {t('survey.approvedDescription')}
          </DetailDiscriptionTypography>
          <DeliveryDate
            component="p"
            variant="bodyDefault"
            data-testid="uiParts-SurveyStatusDetailBox-deliveryDate"
          >
            {t('survey.detailBoxDeliveryDate')}
            {t('survey.deliveryDateFormat', {
              year: getDate?.year,
              month: getDate?.month,
              date: getDate?.dt,
              weekday: getDate?.weekday,
              hour: getDate?.hour,
              minute: getDate?.minute,
            })}
          </DeliveryDate>
        </SurveyDetailContainer>
      )}
      {survey.surveyStatusId === SURVEY_STATUSES.REJECTED && (
        <SurveyDetailContainer
          $status={survey.surveyStatusId}
          data-testid="uiParts-SurveyStatusDetailBox-SurveyDetailContainer"
        >
          <DetailDiscriptionTypography
            sx={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}
            component="p"
            variant="bodyDefault"
            data-testid="uiParts-SurveyStatusDetailBox-Text"
          >
            {t('survey.rejectedDescription')}
          </DetailDiscriptionTypography>
          <RejectedReasonBox reasonText={survey.rejectedReason} />
        </SurveyDetailContainer>
      )}
    </>
  );
};

export default SurveyStatusDetailBox;
